<template>
  <v-container fluid class="pa-4 rtl">
    <!-- Header -->
    <v-card class="header-card mb-4" elevation="2">
      <div class="d-flex align-center justify-space-between flex-wrap ga-3">
        <div>
          <div class="text-h5 font-weight-bold" style="color: #4a4f55">
            الكوارث الطبيعية وتأثيرها
          </div>
          <div class="text-body-2 text-grey-darken-1 mt-1">
            مدى تعرض المرافق للكوارث الطبيعية ومستوى الاستعداد
          </div>
        </div>

        <v-btn
          prepend-icon="mdi-arrow-right"
          variant="text"
          style="color: #4a4f55"
        >
          رجوع
        </v-btn>
      </div>
    </v-card>

    <!-- KPIs -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card warning">
          <div class="kpi-value">{{ floodRiskCount }}</div>
          <div class="kpi-label">مرفق عرضة للفيضانات</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card danger">
          <div class="kpi-value">{{ noEmergencyPlanCount }}</div>
          <div class="kpi-label">بدون خطة طوارئ للكوارث</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card danger">
          <div class="kpi-value">{{ highRiskCount }}</div>
          <div class="kpi-label">مرافق في مناطق عالية الخطر</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card success">
          <div class="kpi-value">{{ fullyPreparedCount }}</div>
          <div class="kpi-label">مرافق مؤمَّنة بالكامل</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">توزيع مخاطر الفيضانات</div>
          <apexchart
            v-if="floodSeries.length"
            type="donut"
            height="320"
            :series="floodSeries"
            :options="donutOptions(floodLabels)"
          />
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">توزيع خطط الطوارئ</div>
          <apexchart
            v-if="emergencyPlanSeries.length"
            type="donut"
            height="320"
            :series="emergencyPlanSeries"
            :options="donutOptions(emergencyPlanLabels)"
          />
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">مستوى الخطر الإجمالي</div>
          <apexchart
            v-if="totalRiskSeries.length"
            type="bar"
            height="320"
            :series="[{ name: 'عدد المرافق', data: totalRiskSeries }]"
            :options="barOptions(totalRiskLabels)"
          />
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">مقارنة المخاطر الطبيعية</div>
          <apexchart
            v-if="riskComparisonLabels.length"
            type="bar"
            height="320"
            :series="riskComparisonSeries"
            :options="barOptions(riskComparisonLabels)"
          />
        </v-card>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-card class="table-card" elevation="1">
      <div class="table-header">مستوى التعرض للكوارث والاستعداد</div>

      <v-table class="custom-table">
        <thead>
          <tr>
            <th>المرفق</th>
            <th>فيضانات</th>
            <th>زلازل</th>
            <th>رياح شديدة</th>
            <th>خطة طوارئ</th>
            <th>سعة الإخلاء</th>
            <th>مستوى الخطر الإجمالي</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in facilities" :key="item.id">
            <td class="font-weight-bold">{{ item.name }}</td>

            <td>
              <v-chip
                :color="riskColor(item.floods)"
                size="small"
                variant="flat"
              >
                {{ item.floods }}
              </v-chip>
            </td>

            <td>
              <v-chip
                :color="riskColor(item.earthquakes)"
                size="small"
                variant="flat"
              >
                {{ item.earthquakes }}
              </v-chip>
            </td>

            <td>
              <v-chip
                :color="riskColor(item.strongWinds)"
                size="small"
                variant="flat"
              >
                {{ item.strongWinds }}
              </v-chip>
            </td>

            <td>
              <v-chip
                :color="planColor(item.emergencyPlan)"
                size="small"
                variant="flat"
              >
                {{ item.emergencyPlan }}
              </v-chip>
            </td>

            <td>{{ item.evacuationCapacity }}</td>

            <td>
              <v-chip
                :color="totalRiskColor(item.totalRisk)"
                size="small"
                variant="flat"
              >
                {{ item.totalRisk }}
              </v-chip>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import api from "@/plugins/api";

const facilities = ref([]);

const fetchNaturalDisasters = async () => {
  try {
    const { data } = await api.get("/natural-disasters");

    facilities.value = data.map((item) => ({
      id: item.id,
      name: item.facility_name || item.name || `استبيان #${item.survey_id}`,

      floods: item.floods || item.flood_risk || "غير محدد",
      earthquakes: item.earthquakes || item.earthquake_risk || "غير محدد",
      strongWinds: item.strong_winds || item.wind_risk || "غير محدد",

      emergencyPlan: item.emergency_plan || item.disaster_plan || "غير محدد",

      evacuationCapacity: item.evacuation_capacity || "—",

      totalRisk: item.total_risk || item.risk_level || "غير محدد",
    }));
  } catch (error) {
    console.error("Natural Disasters API Error:", error);
  }
};

onMounted(fetchNaturalDisasters);

const floodRiskCount = computed(
  () =>
    facilities.value.filter(
      (item) => item.floods === "عالي" || item.floods === "شديد"
    ).length
);

const noEmergencyPlanCount = computed(
  () =>
    facilities.value.filter(
      (item) => item.emergencyPlan === "لا يوجد" || item.emergencyPlan === "لا"
    ).length
);

const highRiskCount = computed(
  () =>
    facilities.value.filter(
      (item) => item.totalRisk === "عالي" || item.totalRisk === "شديد"
    ).length
);

const fullyPreparedCount = computed(
  () =>
    facilities.value.filter(
      (item) => item.emergencyPlan === "كامل" && item.totalRisk === "خفيف"
    ).length
);

const countBy = (field) => {
  const result = {};

  facilities.value.forEach((item) => {
    const value = item[field] || "غير محدد";
    result[value] = (result[value] || 0) + 1;
  });

  return result;
};

const floodData = computed(() => countBy("floods"));
const emergencyPlanData = computed(() => countBy("emergencyPlan"));
const totalRiskData = computed(() => countBy("totalRisk"));

const floodLabels = computed(() => Object.keys(floodData.value));
const floodSeries = computed(() => Object.values(floodData.value));

const emergencyPlanLabels = computed(() =>
  Object.keys(emergencyPlanData.value)
);
const emergencyPlanSeries = computed(() =>
  Object.values(emergencyPlanData.value)
);

const totalRiskLabels = computed(() => Object.keys(totalRiskData.value));
const totalRiskSeries = computed(() => Object.values(totalRiskData.value));

const riskScore = (value) => {
  if (value === "خفيف" || value === "منخفض") return 1;
  if (value === "متوسط") return 2;
  if (value === "عالي") return 3;
  if (value === "شديد") return 4;
  return 0;
};

const riskComparisonLabels = computed(() =>
  facilities.value.map((item) => item.name || "غير محدد")
);

const riskComparisonSeries = computed(() => [
  {
    name: "فيضانات",
    data: facilities.value.map((item) => riskScore(item.floods)),
  },
  {
    name: "زلازل",
    data: facilities.value.map((item) => riskScore(item.earthquakes)),
  },
  {
    name: "رياح شديدة",
    data: facilities.value.map((item) => riskScore(item.strongWinds)),
  },
]);

const donutOptions = (labels = []) => ({
  chart: {
    type: "donut",
  },
  labels: labels.length ? labels : ["غير محدد"],
  legend: {
    position: "bottom",
  },
  dataLabels: {
    enabled: true,
  },
});

const barOptions = (labels = []) => ({
  chart: {
    type: "bar",
    toolbar: {
      show: false,
    },
  },
  xaxis: {
    categories: labels.length ? labels : ["غير محدد"],
    labels: {
      rotate: -45,
    },
  },
  plotOptions: {
    bar: {
      borderRadius: 6,
    },
  },
});

const riskColor = (value) => {
  if (value === "خفيف" || value === "منخفض") return "green";
  if (value === "متوسط") return "orange";
  if (value === "غير محدد") return "blue-grey";
  return "red";
};

const planColor = (value) => {
  if (value === "كامل" || value === "نعم") return "green";
  if (value === "جزئي") return "orange";
  if (value === "غير محدد") return "blue-grey";
  return "red";
};

const totalRiskColor = (value) => {
  if (value === "خفيف" || value === "منخفض") return "green";
  if (value === "متوسط") return "orange";
  if (value === "غير محدد") return "blue-grey";
  return "red";
};
</script>

<style scoped>
.rtl {
  direction: rtl;
}

.header-card {
  border-right: 5px solid #4a4f55;
  border-radius: 18px;
  padding: 20px;
}

.kpi-card {
  border-radius: 18px;
  padding: 20px;
  text-align: center;
}

.kpi-value {
  font-size: 34px;
  font-weight: 800;
}

.kpi-label {
  margin-top: 6px;
  font-size: 13px;
}

.danger {
  background: #fdecea;
  color: #c0392b;
}

.warning {
  background: #fff4e5;
  color: #d4821a;
}

.success {
  background: #eafaf1;
  color: #27ae60;
}

.chart-card,
.table-card {
  border-radius: 18px;
  overflow: hidden;
}

.table-header {
  padding: 18px;
  font-size: 18px;
  font-weight: 700;
  color: #3a3f47;
  border-bottom: 1px solid #eee;
}

.custom-table {
  direction: rtl;
}

:deep(th) {
  font-weight: 800 !important;
  color: #3a3f47 !important;
  white-space: nowrap;
}

:deep(td) {
  font-size: 14px;
  white-space: nowrap;
}
</style>
